import { EventType } from "@/analytics/analytics-events";
import {
  HideCadElementEventProperties,
  IsolateCadElementEventProperties,
} from "@/analytics/cad-bim-events-properties";
import { useSceneEvents } from "@/components/common/scene-events-context";
import { ElementIcon, ElementIconType } from "@/components/ui/icons";
import { Features, selectHasFeature } from "@/store/features/features-slice";
import { useAppSelector } from "@/store/store-hooks";
import { FaroIconButton, useCheckForOverflow } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { Box, Stack, Typography } from "@mui/material";
import { useCallback, useRef, useState, type JSX } from "react";
import { TreeNode, TreeNodeProps } from "../tree-node";
import { TreePopUp } from "../tree-popup";
import { ContextMenuCadModelTree } from "./cad-model-tree-context-menu";
import { CadModelTreeNodeData } from "./cad-model-tree-data";
import {
  highlightModelTreeNode,
  isNodeVisible,
  setNodeVisibility,
  unhighlightCadObjects,
} from "./cad-model-visibility";

/**
 * @returns A node in the CAD model tree
 */
export function CadModelTreeNode({
  node,
  style,
}: TreeNodeProps<CadModelTreeNodeData>): JSX.Element {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const { hasOverflown, checkForOverflow } = useCheckForOverflow();

  const [boxRef, setBoxRef] = useState<HTMLDivElement | null>(null);

  const toggleContextMenu = useCallback(
    (open: boolean) => {
      if (isPopupOpen && open) {
        setIsPopupOpen(false);
      }
    },
    [isPopupOpen],
  );
  const highlightedCadObjects = useRef<number[]>([]);
  const cadSceneEvents = useSceneEvents();
  const visibilityIconActive = useRef(false);

  const cadDebugTools = useAppSelector(
    selectHasFeature(Features.CadDebugTools),
  );

  const highlightNodeIn3DScene = useCallback(() => {
    setIsPopupOpen(true);
    // After clicking on the eye icon, the entry event is triggered again,
    // and we don't want to highlight the node at the moment.
    if (visibilityIconActive.current) {
      return;
    }
    unhighlightCadObjects(
      node.data.cadModelData,
      highlightedCadObjects.current,
    );
    highlightedCadObjects.current.length = 0;
    highlightModelTreeNode(
      node.data,
      highlightedCadObjects.current,
      cadDebugTools,
    );
    cadSceneEvents.invalidateCadScene.emit();
  }, [node, cadSceneEvents, cadDebugTools]);

  const unhighlightNodeIn3DScene = useCallback(() => {
    setIsPopupOpen(false);
    if (highlightedCadObjects.current.length > 0) {
      unhighlightCadObjects(
        node.data.cadModelData,
        highlightedCadObjects.current,
      );
      highlightedCadObjects.current.length = 0;
      cadSceneEvents.invalidateCadScene.emit();
    }
    // reset the flag after move out of the node
    visibilityIconActive.current = false;
  }, [node.data, cadSceneEvents]);

  const changeNodeVisibility = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const isolateVisible = !!event.shiftKey;
      if (event.shiftKey || event.ctrlKey) {
        Analytics.track<IsolateCadElementEventProperties>(
          EventType.isolateCadElement,
          {
            via: "treeEye",
            visibility: isolateVisible,
          },
        );
      } else if (node.data.visibility) {
        Analytics.track<HideCadElementEventProperties>(
          EventType.hideCadElement,
          {
            via: "treeEye",
          },
        );
      } else {
        Analytics.track(EventType.showCadElement);
      }
      if (event.shiftKey || event.ctrlKey) {
        setNodeVisibility(node.data, isolateVisible, true);
      } else {
        setNodeVisibility(node.data, !node.data.visibility, false);
      }
      if (highlightedCadObjects.current.length > 0) {
        unhighlightCadObjects(
          node.data.cadModelData,
          highlightedCadObjects.current,
        );
        highlightedCadObjects.current.length = 0;
      }
      cadSceneEvents.invalidateCadScene.emit();
      // Mark the flag to prevent the entry event from highlighting the CAD again
      visibilityIconActive.current = true;
    },
    [node.data, cadSceneEvents.invalidateCadScene],
  );

  return (
    <Box
      ref={setBoxRef}
      component="div"
      style={{ ...style, height: "100%" }}
      onMouseEnter={highlightNodeIn3DScene}
      onMouseLeave={unhighlightNodeIn3DScene}
    >
      <TreePopUp
        parent={boxRef}
        open={isPopupOpen}
        title={node.data.name}
        isTooltipEnabled={hasOverflown}
      >
        <TreeNode<CadModelTreeNodeData>
          node={node}
          shouldExpandNodeOnClick={false}
        >
          <Stack
            direction="row"
            sx={{ flexGrow: 1, alignItems: "center", overflow: "hidden" }}
          >
            <Typography
              noWrap
              onMouseEnter={(e) => checkForOverflow(e.currentTarget)}
              sx={{ ml: 1, flexGrow: 1, fontSize: "0.875em" }}
            >
              {node.data.name}
            </Typography>
          </Stack>
          <FaroIconButton
            onClick={changeNodeVisibility}
            margin={0.5}
            size="s"
            aria-label="toggle model visibility"
            // Get both the image color and its hover color from the container
            color="inherit"
            hoverColor="inherit"
            // Change the button color to forward the container's color to the image
            sx={{ color: "inherit" }}
          >
            <ElementIcon
              icon={
                isNodeVisible(node.data)
                  ? ElementIconType.Visible
                  : ElementIconType.NonVisible
              }
            />
          </FaroIconButton>
          <ContextMenuCadModelTree
            onToggle={toggleContextMenu}
            idIElementModel3dStream={node.data.idIElementModel3dStream}
            modelMessages={node.data.modelMessages}
          />
        </TreeNode>
      </TreePopUp>
    </Box>
  );
}
